import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { brokerageAPI, serviceAPI } from '../lib/api';
import { Handshake, Loader2, CheckCircle, ArrowRight, Shield, Search, DollarSign, Clock, Target } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

export default function BrokeragePage() {
  const { t } = useLanguage();
  const { user, token } = useAuth();
  const [form, setForm] = useState({ 
    target_domain: '', 
    client_name: '', 
    client_email: '', 
    client_phone: '',
    budget_min: '', 
    budget_max: '',
    purpose: 'business',
    urgency: 'normal',
    additional_notes: '' 
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (token) {
        await brokerageAPI.submit({
          ...form,
          budget_min: parseFloat(form.budget_min) || 0,
          budget_max: parseFloat(form.budget_max) || 0
        });
      } else {
        await serviceAPI.request({ 
          ...form, 
          service_type: 'brokerage',
          name: form.client_name,
          email: form.client_email,
          domain_name: form.target_domain,
          budget: `$${form.budget_min} - $${form.budget_max}`
        });
      }
      setSubmitted(true);
      toast.success('Brokerage request submitted!');
    } catch (err) { 
      toast.error(err.response?.data?.detail || 'Failed to submit'); 
    }
    setLoading(false);
  };

  if (submitted) {
    return (
      <div className="px-6 md:px-12 max-w-7xl mx-auto py-20">
        <div className="max-w-lg mx-auto bg-[#0A0A0A] border border-white/5 rounded-sm p-12 text-center">
          <CheckCircle className="h-12 w-12 text-[#00FF94] mx-auto mb-4" />
          <h3 className="text-xl font-bold text-white mb-2">Request Received!</h3>
          <p className="text-zinc-500">A broker will be assigned to your case within 24 hours. We'll keep you updated on the progress.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="px-6 md:px-12 max-w-7xl mx-auto py-12 md:py-20" data-testid="brokerage-page">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <Handshake className="h-10 w-10 text-[#00E5FF] mx-auto mb-4" />
          <h1 className="text-4xl md:text-5xl font-bold text-white font-['Outfit'] tracking-tight">{t('brokerage.title')}</h1>
          <p className="text-zinc-500 mt-3 max-w-xl mx-auto">{t('brokerage.subtitle')}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-16">
          {[
            { icon: Search, title: 'Research', desc: 'We identify the domain owner and assess acquisition feasibility' },
            { icon: Handshake, title: 'Negotiate', desc: 'Expert brokers handle all communication and price negotiation' },
            { icon: Shield, title: 'Secure Transfer', desc: 'Escrow-protected transfer ensures safe domain handover' },
            { icon: Target, title: 'Success', desc: '85%+ success rate on targeted domain acquisitions' },
          ].map((step, i) => (
            <div key={i} className="p-6 bg-[#0A0A0A] border border-white/5 rounded-sm">
              <step.icon className="h-8 w-8 text-[#00E5FF] mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2 font-['Outfit']">{step.title}</h3>
              <p className="text-sm text-zinc-500">{step.desc}</p>
            </div>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="bg-[#0A0A0A] border border-white/5 rounded-sm p-8 space-y-5" data-testid="brokerage-form">
          <h3 className="text-lg font-semibold text-white font-['Outfit']">Request Brokerage Service</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Your Name</Label>
              <Input 
                value={form.client_name} 
                onChange={(e) => setForm(p => ({ ...p, client_name: e.target.value }))} 
                required
                className="mt-1.5 h-11 bg-[#050505] border-zinc-800 focus:border-[#00E5FF] text-white rounded-sm" 
                data-testid="brokerage-name" 
              />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Email</Label>
              <Input 
                type="email" 
                value={form.client_email} 
                onChange={(e) => setForm(p => ({ ...p, client_email: e.target.value }))} 
                required
                className="mt-1.5 h-11 bg-[#050505] border-zinc-800 focus:border-[#00E5FF] text-white rounded-sm" 
                data-testid="brokerage-email" 
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Target Domain</Label>
              <Input 
                value={form.target_domain} 
                onChange={(e) => setForm(p => ({ ...p, target_domain: e.target.value }))} 
                required
                className="mt-1.5 h-11 bg-[#050505] border-zinc-800 focus:border-[#00E5FF] text-white rounded-sm" 
                placeholder="dream-domain.com" 
                data-testid="brokerage-domain" 
              />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Phone (Optional)</Label>
              <Input 
                value={form.client_phone} 
                onChange={(e) => setForm(p => ({ ...p, client_phone: e.target.value }))}
                className="mt-1.5 h-11 bg-[#050505] border-zinc-800 focus:border-[#00E5FF] text-white rounded-sm" 
                placeholder="+1 555 123 4567"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Budget Min ($)</Label>
              <Input 
                type="number"
                value={form.budget_min} 
                onChange={(e) => setForm(p => ({ ...p, budget_min: e.target.value }))}
                className="mt-1.5 h-11 bg-[#050505] border-zinc-800 focus:border-[#00E5FF] text-white rounded-sm" 
                placeholder="5000" 
                data-testid="brokerage-budget-min" 
              />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Budget Max ($)</Label>
              <Input 
                type="number"
                value={form.budget_max} 
                onChange={(e) => setForm(p => ({ ...p, budget_max: e.target.value }))}
                className="mt-1.5 h-11 bg-[#050505] border-zinc-800 focus:border-[#00E5FF] text-white rounded-sm" 
                placeholder="50000" 
                data-testid="brokerage-budget-max" 
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Purpose</Label>
              <select
                value={form.purpose}
                onChange={(e) => setForm(p => ({ ...p, purpose: e.target.value }))}
                className="w-full mt-1.5 h-11 bg-[#050505] border border-zinc-800 focus:border-[#00E5FF] text-white rounded-sm px-3"
              >
                <option value="business">Business / Brand</option>
                <option value="personal">Personal Project</option>
                <option value="investment">Investment</option>
                <option value="startup">Startup Launch</option>
              </select>
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Urgency</Label>
              <select
                value={form.urgency}
                onChange={(e) => setForm(p => ({ ...p, urgency: e.target.value }))}
                className="w-full mt-1.5 h-11 bg-[#050505] border border-zinc-800 focus:border-[#00E5FF] text-white rounded-sm px-3"
              >
                <option value="flexible">Flexible (no rush)</option>
                <option value="normal">Normal (1-2 months)</option>
                <option value="urgent">Urgent (ASAP)</option>
              </select>
            </div>
          </div>

          <div>
            <Label className="text-zinc-400 text-xs uppercase tracking-wider">Additional Details</Label>
            <Textarea 
              value={form.additional_notes} 
              onChange={(e) => setForm(p => ({ ...p, additional_notes: e.target.value }))} 
              rows={3}
              className="mt-1.5 bg-[#050505] border-zinc-800 focus:border-[#00E5FF] text-white rounded-sm resize-none" 
              placeholder="Tell us why you need this domain and any other relevant information..."
              data-testid="brokerage-details" 
            />
          </div>

          <div className="p-4 bg-zinc-900/50 rounded border border-white/5">
            <p className="text-sm text-zinc-400">
              <strong className="text-white">Commission:</strong> 15% of final acquisition price (only paid on successful acquisition)
            </p>
          </div>

          <Button 
            type="submit" 
            disabled={loading}
            className="h-11 px-8 bg-[#00E5FF] text-black hover:bg-white font-bold uppercase tracking-wider rounded-sm shadow-[0_0_20px_rgba(0,229,255,0.3)]" 
            data-testid="brokerage-submit"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Start Brokerage <ArrowRight className="ml-2 h-4 w-4" /></>}
          </Button>
        </form>
      </div>
    </div>
  );
}
